import { forwardRef, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, Loader2, Maximize2, X } from "lucide-react";
import type { AssetType } from "@/lib/api";
import { positionLabel, swipeDirection } from "@/lib/viewerNav";
import { holdMediaFocus } from "@/lib/mediaFocus";
import { AssetMedia } from "@/components/media/AssetMedia";
import { cn } from "@/lib/utils";

const CHROME_HIDE_MS = 2500;

export interface StagePosition {
  index: number;
  total: number;
  hasMore: boolean;
}

interface Props {
  /** Changes on every step so the media remounts with a short fade. */
  mediaKey: string;
  type: AssetType;
  url: string | null;
  alt: string;
  position?: StagePosition | null;
  onPrev?: () => void;
  onNext?: () => void;
  loadingNext?: boolean;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
}

const chromeButton =
  "pointer-events-auto flex items-center justify-center rounded-full border border-white/15 bg-black/45 text-white backdrop-blur transition-[background-color,opacity,transform] hover:bg-black/70 active:scale-[0.94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white";

function NavButton({ side, label, onClick, children }: { side: "left" | "right"; label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      aria-label={label}
      title={label}
      className={cn(
        chromeButton,
        "absolute top-1/2 h-10 w-10 -translate-y-1/2 opacity-80 hover:opacity-100 sm:h-9 sm:w-9",
        side === "left" ? "left-2 sm:left-3" : "right-2 sm:right-3"
      )}
    >
      {children}
    </button>
  );
}

/**
 * Media area of the generation viewer: previous/next arrows, position counter,
 * fullscreen control and touch swipe. Fullscreen state is owned by the dialog,
 * which also hides its side rail while it is on.
 */
export const GenerationStage = forwardRef<HTMLDivElement, Props>(function GenerationStage(
  { mediaKey, type, url, alt, position, onPrev, onNext, loadingNext = false, fullscreen, onToggleFullscreen },
  ref
) {
  const [chromeVisible, setChromeVisible] = useState(true);
  const hideTimer = useRef<number | null>(null);
  const swipeStart = useRef<{ id: number; x: number; y: number } | null>(null);

  // Grid previews behind the viewer pause while it is open, image or video.
  useEffect(() => holdMediaFocus(), []);

  const revealChrome = useCallback(() => {
    setChromeVisible(true);
    if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    hideTimer.current = fullscreen ? window.setTimeout(() => setChromeVisible(false), CHROME_HIDE_MS) : null;
  }, [fullscreen]);

  useEffect(() => {
    revealChrome();
    return () => {
      if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    };
  }, [revealChrome, mediaKey]);

  const counter = position && position.total > 1 ? positionLabel(position.index, position.total, position.hasMore) : null;
  const chrome = cn("transition-opacity duration-200", chromeVisible ? "opacity-100" : "pointer-events-none opacity-0");

  return (
    <div
      ref={ref}
      data-testid="generation-stage"
      data-fullscreen={fullscreen || undefined}
      className={cn(
        "relative order-first flex shrink-0 items-center justify-center overflow-hidden bg-black",
        fullscreen && !chromeVisible && "cursor-none",
        fullscreen ? "h-full min-h-0 flex-1" : "h-[40dvh] lg:h-auto lg:min-h-0 lg:flex-1"
      )}
      style={{ touchAction: "pan-y pinch-zoom" }}
      onPointerMove={fullscreen ? revealChrome : undefined}
      onPointerDown={(event) => {
        if (fullscreen) revealChrome();
        if (event.pointerType === "mouse") return;
        const target = event.target as HTMLElement;
        if (target.closest("[data-no-swipe], button, input")) return;
        swipeStart.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }}
      onPointerUp={(event) => {
        const start = swipeStart.current;
        swipeStart.current = null;
        if (!start || start.id !== event.pointerId) return;
        const direction = swipeDirection(event.clientX - start.x, event.clientY - start.y);
        if (direction === "next") onNext?.();
        if (direction === "prev") onPrev?.();
      }}
      onPointerCancel={() => {
        swipeStart.current = null;
      }}
    >
      <div key={mediaKey} className="flex h-full w-full items-center justify-center motion-safe:animate-fade-in">
        <AssetMedia
          type={type}
          url={url}
          alt={alt}
          variant="theater"
          className="bg-transparent"
          arrowKeysSeek={false}
          onToggleFullscreen={onToggleFullscreen}
          fullscreenActive={fullscreen}
        />
      </div>

      <div className={cn("pointer-events-none absolute inset-0", chrome)}>
        {counter && (
          <span
            aria-live="polite"
            className={cn(
              "absolute left-3 rounded-md bg-black/55 px-2 py-0.5 text-[11px] font-medium tabular-nums text-white/90 backdrop-blur",
              fullscreen ? "top-[calc(env(safe-area-inset-top)+0.75rem)]" : "top-3"
            )}
          >
            <span className="sr-only">Generation </span>
            {counter}
          </span>
        )}
        {onPrev && (
          <NavButton side="left" label="Previous generation" onClick={onPrev}>
            <ChevronLeft className="h-5 w-5" />
          </NavButton>
        )}
        {(onNext || loadingNext) && (
          <NavButton side="right" label={loadingNext ? "Loading more generations" : "Next generation"} onClick={() => onNext?.()}>
            {loadingNext ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-5 w-5" />}
          </NavButton>
        )}
        {fullscreen ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleFullscreen();
            }}
            aria-label="Exit fullscreen"
            title="Exit fullscreen (Esc)"
            className={cn(chromeButton, "absolute right-3 top-[calc(env(safe-area-inset-top)+0.75rem)] h-10 w-10 sm:h-9 sm:w-9")}
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          // Video keeps the player's own fullscreen button in its control bar.
          type === "image" &&
          url && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggleFullscreen();
              }}
              aria-label="Enter fullscreen"
              title="Fullscreen (F)"
              className={cn(chromeButton, "absolute bottom-3 right-3 h-10 w-10 rounded-lg sm:h-9 sm:w-9")}
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )
        )}
      </div>
    </div>
  );
});
